import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { RoomEvent } from 'livekit-client'

/** The room events that can change who is in it: arrivals, departures, and the connection (a drop empties it). */
const MEMBERSHIP_EVENTS = [RoomEvent.ParticipantConnected, RoomEvent.ParticipantDisconnected, RoomEvent.ConnectionStateChanged]

/**
 * A value read from who is in the room now (`present` is `getParticipantByIdentity`, this tab's own participant
 * included), read again on every membership event. Re-renders only when the value changes, so keep it a boolean or a
 * number (or a string that stands for several).
 */
function useMembership<T extends boolean | number | string>(read: (present: (identity: string) => boolean) => T): T {
  const room = useRoomContext()
  const subscribe = useCallback(
    (onChange: () => void) => {
      MEMBERSHIP_EVENTS.forEach((event) => room.on(event, onChange))
      return () => {
        MEMBERSHIP_EVENTS.forEach((event) => room.off(event, onChange))
      }
    },
    [room],
  )
  return useSyncExternalStore(subscribe, () => read((identity) => room.getParticipantByIdentity(identity) !== undefined))
}

/**
 * Whether any of `identities` is in the room now, this tab's own participant included (`getParticipantByIdentity`).
 * A seat in the room's metadata can outlive its participant (a seat the API hasn't freed yet), so a claim that someone
 * is live on the stage checks both. Re-renders only when the answer changes, not on every arrival, so a page can call
 * it. Use inside the room's context.
 */
export function useInRoom(identities: readonly string[]): boolean {
  return useMembership((present) => identities.some(present))
}

/**
 * How many of `identities` are in the room now, as `useInRoom` asks it: the seats on a stage whose participant is
 * still there. Re-renders whenever the count changes, so call it in the component that shows the count, not a page.
 */
export function useInRoomCount(identities: readonly string[]): number {
  return useMembership((present) => identities.filter(present).length)
}

/**
 * `useInRoomCount` for several groups at once (each stage's seats, for the stage switcher): how many of each are in the
 * room now, in the groups' order. Re-renders when any count changes; the same array comes back while none does.
 */
export function useInRoomCounts(groups: readonly (readonly string[])[]): readonly number[] {
  const key = useMembership((present) => groups.map((identities) => identities.filter(present).length).join(','))
  return useMemo(() => (key ? key.split(',').map(Number) : []), [key])
}
